// SPDX-License-Identifier: LicenseRef-Proprietary

import React from 'react';
import Plot from 'react-plotly.js';
import type { VaccineOutputs } from '../lib/models';

interface EfficacyChartProps {
  model: VaccineOutputs;
  title?: string;
}

export const EfficacyChart: React.FC<EfficacyChartProps> = ({
  model,
  title = 'Vaccination Efficacy: Health Benefits',
}) => {
  // Safe value extraction with defaults
  // Ensure all values are positive (benefits should always be positive)
  const casesAvoided = Math.abs(model.incremental?.casesAvoided ?? 0);
  const qalyGained = Math.abs(model.incremental?.qalyGained ?? 0);
  const deathsAverted = Math.abs(model.incremental?.deathsAverted ?? 0);
  const hospitalizationsAvoided = Math.abs(model.incremental?.hospitalizationsAvoided ?? 0);

  // Calculate max value and add padding for outside text labels
  const maxValue = Math.max(casesAvoided, qalyGained, deathsAverted, hospitalizationsAvoided);
  const yAxisMax = maxValue > 0 ? maxValue * 1.15 : undefined; // Add 15% padding at top

  const plotData = [
    {
      x: ['Events Prevented', 'QALYs Gained', 'Deaths Averted', 'Hospitalizations Avoided'],
      y: [
        casesAvoided,
        qalyGained,
        deathsAverted,
        hospitalizationsAvoided,
      ],
      type: 'bar' as const,
      marker: {
        color: ['#4C8DFF', '#5EDFCB', '#6A5BE8', '#8BB0FF'], // Standardized colors matching Cost Composition
        line: {
          color: '#1e293b',
          width: 1,
        },
      },
      text: [
        `${Math.round(casesAvoided).toLocaleString('fi-FI')} cases`,
        `${Math.round(qalyGained).toLocaleString('fi-FI')} QALYs`,
        `${Math.round(deathsAverted).toLocaleString('fi-FI')} deaths`,
        `${Math.round(hospitalizationsAvoided).toLocaleString('fi-FI')} admissions`,
      ],
      textposition: 'outside' as const,
      textfont: {
        color: '#f1f5f9',
        size: 12,
      },
      hovertemplate: '<b>%{x}</b><br>%{text}<extra></extra>',
    },
  ];

  const layout = {
    title: {
      text: title,
      font: { size: 16, color: '#f1f5f9' },
    },
    xaxis: {
      title: { text: '', font: { color: '#94a3b8' } },
      tickfont: { color: '#94a3b8', size: 11 },
      gridcolor: '#334155',
    },
    yaxis: {
      title: { text: 'Count', font: { color: '#94a3b8', size: 13 } },
      tickfont: { color: '#94a3b8', size: 11 },
      gridcolor: '#334155',
      tickformat: ',.0f', // Whole numbers with thousand separators for better readability
      dtick: undefined, // Let Plotly auto-determine tick spacing
      // Add padding at top to accommodate outside text labels
      range: yAxisMax ? [0, yAxisMax] : undefined,
    },
    showlegend: false,
    margin: { l: 60, r: 20, t: 100, b: 100 },
    autosize: true,
    hovermode: 'closest' as const,
    plot_bgcolor: 'rgba(0,0,0,0)',
    paper_bgcolor: 'rgba(0,0,0,0)',
  };

  const config = {
    displayModeBar: true,
    displaylogo: false,
    responsive: true,
    modeBarButtonsToRemove: ['pan2d', 'lasso2d', 'select2d'] as any,
  };

  return (
    <>
    <div className="vaccine-demo__chart-container">
      <div className="vaccine-demo__chart-header">
        <div className="vaccine-demo__chart-title">{title}</div>
      </div>
      <div className="vaccine-demo__chart">
        <Plot
          data={plotData}
          layout={layout}
          config={config}
          style={{ width: '100%', height: '500px' }}
          useResizeHandler
        />
      </div>
    </div>
    </>
  );
};
